/**
 * Mode de réponse "calculatrice" (généré par le Blueprint paramétrique) :
 * au lieu d'un pad numérique classique, l'élève compose une EXPRESSION à
 * partir de variables (nombres de l'énoncé + résultats des questions
 * précédentes) et d'opérateurs. La valeur affichée EST sa réponse — validée
 * avec tolérance côté serveur (RPC `submit_block_answer_numeric`, seul juge).
 *
 * Réutilise l'évaluateur d'expressions partagé (@jeraisonne/db/shared/expr,
 * le même que la géométrie contrainte et le moteur de génération du
 * Blueprint) pour l'aperçu live — jamais de confiance dans le résultat côté
 * client pour le scoring, uniquement pour l'affichage.
 */

import { useState } from 'react';
import { Delete, Check } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { evalExpr } from '@jeraisonne/db/shared/expr';
import { calculatorCheckpointData } from '@/lib/exercise-v2/block-helpers';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';
import { Spinner } from '@/components/spinner';

const KEYS = [
  '7',
  '8',
  '9',
  '÷',
  '4',
  '5',
  '6',
  '×',
  '1',
  '2',
  '3',
  '-',
  '0',
  '.',
  '(',
  ')',
  '+',
] as const;

/** L'expression affichée garde les glyphes tapés (×/÷) — seule l'évaluation les convertit. */
function toEvalSyntax(expr: string): string {
  return expr.replace(/×/g, '*').replace(/÷/g, '/');
}

export interface VariableExpressionAnswerProps {
  checkpoint: ExerciseBlock;
  answerValue: number | undefined;
  wasCorrect: boolean | undefined;
  pending: boolean;
  /** La réponse n'a pas pu partir : message sous le bouton. */
  error?: string | null;
  onSubmit: (value: number) => void | Promise<void>;
}

export function VariableExpressionAnswer({
  checkpoint,
  answerValue,
  wasCorrect,
  pending,
  error = null,
  onSubmit,
}: VariableExpressionAnswerProps) {
  const [expression, setExpression] = useState('');
  const data = calculatorCheckpointData(checkpoint);
  const answered = answerValue !== undefined;

  if (!data) return null;

  const scope = Object.fromEntries(
    data.availableVariables.map((v) => [v.label.toLowerCase(), v.value]),
  );
  let preview: number | null = null;
  if (expression.trim()) {
    try {
      const v = evalExpr(toEvalSyntax(expression), scope);
      if (Number.isFinite(v)) preview = v;
    } catch {
      preview = null;
    }
  }

  function press(key: string) {
    if (answered) return;
    setExpression((prev) => prev + key);
  }
  function backspace() {
    if (answered) return;
    setExpression((prev) => prev.slice(0, -1));
  }
  function clear() {
    if (answered) return;
    setExpression('');
  }

  return (
    <div className="space-y-3">
      {/* Variables disponibles : paramètres de l'énoncé + résultats des questions précédentes. */}
      {data.availableVariables.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {data.availableVariables.map((v) => (
            <button
              key={v.label}
              type="button"
              disabled={answered}
              onClick={() => press(v.label)}
              className="border-primary-200 bg-primary-50 text-primary-700 min-h-[2.75rem] rounded-xl border px-3 text-sm font-semibold active:scale-[0.97] disabled:opacity-50"
            >
              {v.label} = {v.value}
            </button>
          ))}
        </div>
      )}

      {/* Aperçu de l'expression composée. */}
      <div
        className={cn(
          'border-ink-200 min-h-[3.5rem] rounded-xl border bg-white px-4 py-2.5 font-mono text-lg',
          answered && wasCorrect && 'border-xp-300 bg-xp-50',
          answered && !wasCorrect && 'border-error-300 bg-error-50',
        )}
      >
        <div className="text-ink-900 break-all">
          {expression || <span className="text-ink-300">…</span>}
        </div>
        {preview !== null && !answered && <div className="text-ink-400 text-sm">= {preview}</div>}
        {answered && (
          <div className="text-ink-500 text-sm">
            Réponse soumise : {answerValue}
            {data.unitLabel ? ` ${data.unitLabel}` : ''}
          </div>
        )}
      </div>

      {!answered && (
        <>
          <div className="grid grid-cols-4 gap-1.5">
            {KEYS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => press(k)}
                className="border-ink-200 text-ink-800 bg-ink-50 min-h-[2.75rem] rounded-xl border text-base font-semibold active:scale-[0.97]"
              >
                {k}
              </button>
            ))}
            <button
              type="button"
              onClick={backspace}
              aria-label="Effacer le dernier caractère"
              className="border-ink-200 text-ink-600 bg-ink-50 flex min-h-[2.75rem] items-center justify-center rounded-xl border active:scale-[0.97]"
            >
              <Delete className="h-4 w-4" />
            </button>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <button
              type="button"
              onClick={clear}
              className="border-ink-200 text-ink-600 min-h-[2.75rem] w-full rounded-xl border bg-white text-sm font-semibold active:scale-[0.98] sm:w-auto sm:px-4"
            >
              Effacer tout
            </button>
            <button
              type="button"
              disabled={preview === null || pending}
              aria-busy={pending || undefined}
              onClick={() => preview !== null && void onSubmit(preview)}
              className={cn(
                'bg-primary-500 min-h-[2.75rem] w-full flex-1 rounded-xl text-sm font-bold text-white active:scale-[0.98]',
                pending ? 'disabled:opacity-80' : 'disabled:opacity-40',
              )}
            >
              <span className="flex items-center justify-center gap-2">
                {pending ? (
                  <>
                    <Spinner className="h-4 w-4" /> Envoi de ta réponse…
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" /> Valider cette réponse
                  </>
                )}
              </span>
            </button>
          </div>
          {error && !pending && (
            <p role="alert" className="text-error-600 font-sans text-sm font-semibold">
              {error}
            </p>
          )}
        </>
      )}
    </div>
  );
}
